import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {PhysicianViewModel} from 'src/app/shared/models/ocl';
import {DateHelperService} from 'src/app/shared/services/general/date-helper.service';
import {ScreenService} from '../../../../shared/services/general';
import {PatientEditable} from '../../models/order-entry/patient';
import {PatientService} from '../../services';

@Component({
  selector: 'app-patient-physician-header',
  templateUrl: './patient-physician-header.component.html',
  styleUrls: ['./patient-physician-header.component.scss'],
})
export class PatientPhysicianHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public patientControl?: UntypedFormControl;
  @Input() public physicianControl?: UntypedFormControl;
  @Input() public signature?: any;
  @Output() public patientSelectionChanged = new EventEmitter();
  @Input() public disabled = false;
  public innerSignature?: any;
  public patient?: PatientEditable;
  public physician?: PhysicianViewModel;
  public isPhone: boolean = false;
  public isPhoneOrTablet: boolean = false;
  public lastPhysician?: PhysicianViewModel;
  public sameAsPrimary = false;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private dateHelper: DateHelperService,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private screen: ScreenService,
    private ref: ChangeDetectorRef
  ) {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.signature && changes.signature.currentValue !== undefined) {
      this.innerSignature = changes.signature.currentValue;
    }
  }

  public ngOnInit() {
    if ((this.route.snapshot.paramMap as any).params.patientId !== undefined) {
      this.patientService.getPatient((this.route.snapshot.paramMap as any).params.patientId)
        .pipe(take(1)).subscribe(patient => this.patient = patient);
    }
    this.patientControl?.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(p => {
      this.patient = p;
      this.patientSelectionChanged.emit(p);
    });
    this.physicianControl?.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(p => (this.physician = p));

    this.isPhone = this.screen.sizes['screen-x-small'];
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhone = this.screen.sizes['screen-x-small'];
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    });
  }

  public sameAsPrimaryPhysician(value: any) {
    this.sameAsPrimary = value;
    if (value) {
      this.lastPhysician = this.physician;
      const physician = this.patient && this.patient.primaryPhysician ? this.patient.primaryPhysician : undefined;
      this.physicianControl?.setValue(physician);
    } else {
      this.physicianControl?.setValue(this.lastPhysician);
    }
  }

  public get Client() {
    return this.patient ? (this.patient.client ? this.patient.client.name : '') : '';
  }

  public get PatientFullName() {
    return this.patient ? `${this.patient.firstName} ${this.patient.middleName
      ? this.patient.middleName : ''} ${this.patient.lastName}` : '';
  }

  public get DOB() {
    if (!this.patient) return '';
    if (!this.patient.birthDate) return '';
    return this.dateHelper.dateWithFormatWithoutUtc(this.patient.birthDate as Date, 'LL');
  }

  public get Gender() {
    return this.patient ? this.patient.gender : '';
  }

  public get Room() {
    return this.patient ? (this.patient.room ? this.patient.room.name : '') : '';
  }

  public get Unit() {
    if (!this.patient) {
      return '';
    }
    if (!this.patient.unit) {
      return '';
    }
    return this.patient ? this.patient.unit.name : '';
  }

  public get Physician() {
    return this.physician ? `${this.physician.lastName}, ${this.physician.firstName}` : '';
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
